package diana.soleil.design.iraniancanadienbazzar.core.common

import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow

/**
 * The social networks pinned as first-class targets on the app's share sheet (owner decision
 * 2026-07-18: Telegram, WhatsApp, Instagram, and Facebook are the community's channels — they
 * lead every share, with the OS sheet demoted to "More"). [displayName] is the trademarked brand
 * name — identical across EN/FR/FA, so it is data here rather than a translated resource.
 */
enum class ShareTarget(val displayName: String) {
    Telegram("Telegram"),
    WhatsApp("WhatsApp"),
    Instagram("Instagram"),
    Facebook("Facebook"),
}

/**
 * App-wide sharing. `share(text)` no longer jumps straight to the OS chooser — it raises
 * [pendingShare], and the app shell renders the Mahaleh share sheet (Telegram/WhatsApp/Instagram/
 * Facebook first, then copy-link and the OS sheet as "More"). Platform hooks:
 * [systemShare] = the old chooser (Android Intent / iOS UIActivityViewController / web
 * navigator.share), [openUrl] = open an external URL (share deep links). Previews/fakes keep
 * no-op defaults.
 */
class ShareService(
    private val systemShareHandler: (String) -> Unit,
    private val openUrlHandler: (String) -> Unit = {},
) {

    private val _pendingShare = MutableStateFlow<String?>(null)

    /** The share message awaiting a target choice; the app shell shows the sheet while non-null. */
    val pendingShare: StateFlow<String?> = _pendingShare.asStateFlow()

    /** Entry point used by every share button: raises the in-app share sheet for [text]. */
    fun share(text: String) {
        _pendingShare.value = text
    }

    /** Sheet dismissed or completed. */
    fun clearPending() {
        _pendingShare.value = null
    }

    /** The OS share sheet ("More" on the Mahaleh sheet). */
    fun systemShare(text: String) = systemShareHandler(text)

    /**
     * Opens [target]'s share flow for [text]. Telegram/WhatsApp/Facebook have universal share
     * URLs (they deep-link into the installed apps via their verified domains). Instagram has NO
     * pre-filled share URL — the caller copies the link first (see the sheet's Instagram hint)
     * and this opens Instagram for the user to paste into a story/DM.
     */
    fun shareTo(target: ShareTarget, text: String) {
        val url = extractUrl(text)
        openUrlHandler(
            when (target) {
                ShareTarget.Telegram ->
                    "https://t.me/share/url?url=${encode(url ?: "")}&text=${encode(textWithoutUrl(text, url))}"
                ShareTarget.WhatsApp ->
                    "https://wa.me/?text=${encode(text)}"
                ShareTarget.Facebook ->
                    "https://www.facebook.com/sharer/sharer.php?u=${encode(url ?: text)}"
                ShareTarget.Instagram ->
                    "https://www.instagram.com/"
            },
        )
    }

    /** The shareable link inside a share message (our messages are "Title\nhttps://mahaleh.ca/…"). */
    fun extractUrl(text: String): String? =
        text.split(Regex("\\s+")).lastOrNull { it.startsWith("http://") || it.startsWith("https://") }

    private fun textWithoutUrl(text: String, url: String?): String =
        if (url == null) text else text.replace(url, "").trim()

    /** Minimal RFC 3986 percent-encoding for query values (KMP — no java.net here). */
    private fun encode(value: String): String = buildString {
        for (byte in value.encodeToByteArray()) {
            val c = byte.toInt().toChar()
            if (c in 'A'..'Z' || c in 'a'..'z' || c in '0'..'9' || c == '-' || c == '.' || c == '_' || c == '~') {
                append(c)
            } else {
                append('%')
                append(((byte.toInt() shr 4) and 0xF).toString(16).uppercase())
                append((byte.toInt() and 0xF).toString(16).uppercase())
            }
        }
    }
}
